import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services';

/**
 * Theme Context
 * Manages dark/light mode across the application
 */

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState('light');
  const [isLoading, setIsLoading] = useState(true);

  // Apply theme to document
  const applyTheme = useCallback((newTheme) => {
    const root = document.documentElement;

    if (newTheme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else if (newTheme === 'light') {
      root.classList.remove('dark');
      root.classList.add('light');
    } else if (newTheme === 'auto') {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
        root.classList.remove('light');
      } else {
        root.classList.remove('dark');
        root.classList.add('light');
      }
    }
  }, []);

  // Load theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // First check localStorage for immediate theme application
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
          setThemeState(savedTheme);
          applyTheme(savedTheme);
        }

        // If authenticated, fetch from API
        if (authService.isAuthenticated()) {
          const response = await authService.getSettings();
          if (response.success && response.data?.preferences?.theme) {
            const apiTheme = response.data.preferences.theme;
            setThemeState(apiTheme);
            applyTheme(apiTheme);
            localStorage.setItem('theme', apiTheme);
          }
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        // Fall back to light theme
        applyTheme('light');
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [applyTheme]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (theme !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const root = document.documentElement;
      if (e.matches) {
        root.classList.add('dark');
        root.classList.remove('light');
      } else {
        root.classList.remove('dark');
        root.classList.add('light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Set theme (updates state, localStorage, and DOM)
  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  }, [applyTheme]);

  // Get current effective theme (resolves 'auto' to actual theme)
  const getEffectiveTheme = useCallback(() => {
    if (theme === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  }, [theme]);

  // Check if dark mode is active
  const isDark = getEffectiveTheme() === 'dark';

  const value = {
    theme,
    setTheme,
    isDark,
    isLoading,
    getEffectiveTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
